import { Component, type CSSProperties, type ErrorInfo, type ReactNode } from 'react';
import { log } from '@embrace-io/web-sdk';
import { claimChunkReload, isChunkLoadError, releaseChunkReload } from '@utils/chunk-reload';

/**
 * Last-resort boundary around the whole app.
 *
 * Without it, any throw during render — or a rejected `lazy()` import — unmounts
 * the entire tree and leaves `#root` empty, so the user sees nothing but the
 * body background and we get no signal at all. That is the failure mode this
 * exists to prevent: never a blank page, always something the user can read
 * back to us.
 *
 * The fallback deliberately uses no Mantine, no theme context and no router.
 * It has to survive a crash that came from any of them.
 */

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
  componentStack: string;
}

const panel: CSSProperties = {
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '24px',
  background: '#ffffff',
  color: '#111827',
  fontFamily: "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
};

const button: CSSProperties = {
  padding: '8px 16px',
  borderRadius: '8px',
  border: '1px solid #d1d5db',
  background: '#ffffff',
  color: '#111827',
  fontSize: '14px',
  cursor: 'pointer',
};

class RootErrorBoundary extends Component<Props, State> {
  override state: State = { error: null, componentStack: '' };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    this.setState({ componentStack: info.componentStack ?? '' });

    try {
      log.logException(error, {
        handled: true,
        attributes: {
          'jarvis.error_boundary': 'root',
          'jarvis.route': window.location.pathname,
          'jarvis.query': window.location.search,
          'jarvis.component_stack': (info.componentStack ?? '').slice(0, 4000),
        },
      });
    } catch {
      // Never let reporting mask the original error.
    }

    console.error('[RootErrorBoundary]', error, info.componentStack);

    // A missing chunk means the tab is running against a deploy that is gone.
    // One automatic reload picks up the current index.html; claimChunkReload
    // stops that from looping when the asset is really absent.
    if (isChunkLoadError(error) && claimChunkReload()) {
      window.location.reload();
    }
  }

  private details(): string {
    const { error, componentStack } = this.state;
    return [
      `URL: ${window.location.href}`,
      `Time: ${new Date().toISOString()}`,
      `UA: ${navigator.userAgent}`,
      '',
      `${error?.name ?? 'Error'}: ${error?.message ?? ''}`,
      '',
      error?.stack ?? '',
      '',
      'Component stack:',
      componentStack,
    ].join('\n');
  }

  private handleCopy = () => {
    navigator.clipboard?.writeText(this.details()).catch(() => {
      // Clipboard blocked (insecure context / permission) — the text is on
      // screen anyway, so a screenshot still works.
    });
  };

  private handleReload = () => {
    // Manual retry: let the automatic path try again too.
    releaseChunkReload();
    window.location.reload();
  };

  override render() {
    const { error, componentStack } = this.state;

    if (!error) {
      return this.props.children;
    }

    return (
      <div style={panel}>
        <div style={{ maxWidth: '720px', width: '100%' }}>
          <h1 style={{ fontSize: '20px', fontWeight: 600, margin: '0 0 8px' }}>Halaman gagal dimuat</h1>
          <p style={{ fontSize: '14px', color: '#4b5563', margin: '0 0 20px', lineHeight: 1.6 }}>
            Terjadi kesalahan saat menampilkan halaman ini. Data Anda tidak hilang. Coba muat ulang, dan jika masih
            gagal kirimkan detail di bawah ini ke tim teknis.
          </p>

          <div style={{ display: 'flex', gap: '8px', marginBottom: '20px' }}>
            <button type="button" onClick={this.handleReload} style={button}>
              Muat Ulang
            </button>
            <button type="button" onClick={this.handleCopy} style={button}>
              Salin Detail Error
            </button>
            <button type="button" onClick={() => window.location.assign('/doc-pabean')} style={button}>
              Kembali ke Daftar Dokumen
            </button>
          </div>

          <details open>
            <summary style={{ fontSize: '13px', color: '#4b5563', cursor: 'pointer', marginBottom: '8px' }}>
              Detail teknis
            </summary>
            <pre
              style={{
                fontSize: '12px',
                lineHeight: 1.5,
                background: '#f9fafb',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                padding: '12px',
                maxHeight: '320px',
                overflow: 'auto',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                margin: 0,
              }}
            >
              {`${error.name}: ${error.message}\n\n${error.stack ?? ''}\n${componentStack}`}
            </pre>
          </details>
        </div>
      </div>
    );
  }
}

export default RootErrorBoundary;
